import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Plus, Search, X } from 'lucide-react'
import type { CatalogItem } from '@/services/catalog'

type MultiSelectChipsProps = {
  label?: string
  options: CatalogItem[]
  value: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
  disabled?: boolean
  /**
   * dropdown — full field opens menu (list filters).
   * chips-plus — chips + (+) button; + only when not disabled (detail edit mode).
   */
  variant?: 'dropdown' | 'chips-plus'
}

export function MultiSelectChips({
  label = '',
  options,
  value,
  onChange,
  placeholder = 'Chọn...',
  disabled = false,
  variant = 'dropdown',
}: MultiSelectChipsProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  const selected = useMemo(
    () => options.filter((item) => value.includes(item.id)),
    [options, value],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return options.filter((item) => {
      if (value.includes(item.id)) return false
      if (!q) return true
      return (
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q)
      )
    })
  }, [options, query, value])

  function toggle(id: string) {
    if (disabled) return
    if (value.includes(id)) onChange(value.filter((x) => x !== id))
    else onChange([...value, id])
  }

  const menu = open && !disabled ? (
    <div className="absolute z-30 mt-1 w-full min-w-[200px] overflow-hidden rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white shadow-lg">
      <div className="relative border-b border-slate-100 p-2">
        <Search className="pointer-events-none absolute top-1/2 left-4 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              setOpen(false)
              setQuery('')
            }
          }}
          placeholder={label ? `Tìm ${label.toLowerCase()}...` : 'Tìm...'}
          className="w-full rounded-[var(--radius-sm)] border border-slate-200 py-1.5 pr-2 pl-8 text-sm outline-none focus:border-[var(--color-accent)]"
        />
      </div>
      <ul className="max-h-48 overflow-auto py-1 text-sm">
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-[var(--color-text-muted)]">
            Không còn mục phù hợp
          </li>
        ) : (
          filtered.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50"
                onClick={() => {
                  toggle(item.id)
                  setQuery('')
                }}
              >
                <span className="font-mono text-xs text-slate-400">
                  {item.code}
                </span>
                <span>{item.name}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null

  if (variant === 'chips-plus') {
    return (
      <div ref={rootRef} className="relative">
        {label ? (
          <div className="mb-1 text-xs font-medium text-[var(--color-text-muted)]">
            {label}
          </div>
        ) : null}
        <div className="flex min-h-8 flex-wrap items-center gap-1.5">
          {selected.length ? (
            selected.map((item) => (
              <span
                key={item.id}
                className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--color-accent-soft)] px-2 py-0.5 text-xs font-medium text-[var(--color-accent)]"
                title={item.code}
              >
                {item.name}
                {!disabled ? (
                  <button
                    type="button"
                    className="rounded hover:bg-emerald-100"
                    aria-label={`Bỏ ${item.name}`}
                    onClick={() => toggle(item.id)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null}
              </span>
            ))
          ) : (
            <span className="text-sm text-[var(--color-text-muted)]">—</span>
          )}
          {!disabled ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] border border-dashed border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]"
              title="Thêm"
              aria-label="Thêm"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        {menu}
      </div>
    )
  }

  return (
    <div ref={rootRef} className="relative">
      {label ? (
        <div className="mb-1 text-xs font-medium text-[var(--color-text-muted)]">
          {label}
        </div>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 py-1.5 text-left text-sm outline-none hover:border-slate-300 focus:border-[var(--color-accent)] disabled:opacity-50"
      >
        {selected.length ? (
          selected.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--color-accent-soft)] px-2 py-0.5 text-xs font-medium text-[var(--color-accent)]"
            >
              {item.name}
              <span
                role="button"
                tabIndex={-1}
                className="rounded hover:bg-emerald-100"
                onClick={(e) => {
                  e.stopPropagation()
                  toggle(item.id)
                }}
              >
                <X className="h-3 w-3" />
              </span>
            </span>
          ))
        ) : (
          <span className="text-[var(--color-text-muted)]">{placeholder}</span>
        )}
        <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {menu}
    </div>
  )
}
